import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'
import dayjs from 'dayjs'
import * as XLSX from 'xlsx'
import { Download } from 'lucide-react'
import { Button } from 'vnetwork-ui'
import { IAction } from 'vnetwork-ui/dist/components/GokuDataGrid/Actions'

import GokuDataGrid from '@/components/GokuDataGrid'
import SubHeaders from '@/components/Headers/SubHeaders'
import { generatePathDatacentral, paths } from '@/utils/path'
import { getAffiliatesList } from '@/modules/Affiliate/services'
import { Affiliate } from '@/modules/Affiliate/types'
import { affiliateListColumns } from '../List/Active/columns'
import ActiveFilters from '../List/Active/Filters'
import EyeIcons from '@/icons/EyeIcons'

// Employee affiliate list: reuses the exact customer affiliate list (columns, filters, grid),
// scoped to referrer_type='employee'. Rows open the standard affiliate detail.
const FILTER_DEFAULT_VALUES = { status: '', tier: '' }

// The export shape is dictated by the mail-campaign importer, not by this grid. ReceiverImporter
// (MailerCampaign/components/shared) reads only the FIRST sheet, refuses a file with no column
// literally named `email`, and turns every other column into a template variable named after that
// column. So these headers ARE the variable vocabulary the mail template writes against —
// `${ma_gioi_thieu}`, `${link_gioi_thieu}`. Renaming one here silently breaks every template using it.
const EXPORT_SHEET_NAME = 'Receivers'
const EXPORT_COLUMNS = ['email', 'name', 'ma_gioi_thieu', 'link_gioi_thieu']

// Fetched in one shot rather than page by page: GET /affiliates passes `limit` straight through with
// no upper clamp (affiliate.types.go — `form:"limit,default=10"`), and this list is employees only,
// so it is tens of rows, not thousands.
const EXPORT_PAGE_SIZE = 5000

const EmployeeAffiliateList = () => {
    const navigate = useNavigate()
    const { t } = useTranslation('affiliate')
    const [isExporting, setIsExporting] = useState(false)

    const actions: IAction<Affiliate>[] = [
        {
            onClick: (row) =>
                navigate(
                    generatePathDatacentral(paths.affiliateDetail, {
                        id: String(row?.affiliate_id),
                    })
                ),
            title: t('view'),
            color: 'primary',
            icon: <EyeIcons />,
        },
    ]

    const handleExport = async () => {
        setIsExporting(true)

        try {
            const response = await getAffiliatesList({
                page: 1,
                page_size: EXPORT_PAGE_SIZE,
                q: '',
                referrer_type: 'employee',
            })

            const rows = response?.results || []
            if (rows.length === 0) {
                toast.error(t('export_no_data'))
                return
            }

            // Rows without an email are exported on purpose. The campaign importer drops them
            // *silently*, so leaving them in the file is the only way the gap stays visible — and
            // the toast below reports the count instead of letting it pass unnoticed.
            const data = rows.map((row) => ({
                email: row?.referrer_email || '',
                name: row?.referrer_name || '',
                ma_gioi_thieu: row?.referral_code || '',
                link_gioi_thieu: row?.referral_link || '',
            }))

            const worksheet = XLSX.utils.json_to_sheet(data, {
                header: EXPORT_COLUMNS,
            })
            const workbook = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(workbook, worksheet, EXPORT_SHEET_NAME)
            XLSX.writeFile(
                workbook,
                `affiliate-employees-${dayjs().format('YYYYMMDD-HHmmss')}.xlsx`
            )

            const missingEmail = data.filter((row) => !row.email).length
            if (missingEmail > 0) {
                toast(
                    t('export_missing_email', {
                        missing: missingEmail,
                        total: data.length,
                    })
                )
                return
            }

            toast.success(t('export_success', { total: data.length }))
        } catch (_error) {
            toast.error(t('export_error'))
        } finally {
            setIsExporting(false)
        }
    }

    return (
        <div>
            <SubHeaders
                breadcrumbs={[
                    {
                        path: paths.employeeAffiliatesList,
                        title: `${t('sidebar_title')} — ${t('sidebar_employee')}`,
                    },
                ]}
                components={[
                    <Button
                        key="export-receivers"
                        variant="outlined"
                        onClick={handleExport}
                        disabled={isExporting}
                    >
                        <span className="flex items-center gap-2">
                            <Download className="w-4 h-4" />
                            {isExporting ? t('exporting') : t('export_excel')}
                        </span>
                    </Button>,
                ]}
            />
            <GokuDataGrid
                apiCall={(payload) =>
                    getAffiliatesList({ ...payload, referrer_type: 'employee' })
                }
                columns={affiliateListColumns(t)}
                reactQueryKey="affiliate-employee"
                actions={actions}
                dataGridProps={{
                    getRowId: (row) => row?.affiliate_id,
                }}
                searchPlaceholder={t('search_affiliate')}
                filterDefaultValues={FILTER_DEFAULT_VALUES}
            >
                {({ control, watch, setValue }) => (
                    <ActiveFilters
                        control={control}
                        watch={watch}
                        setValue={(name, value) => setValue(name as any, value)}
                    />
                )}
            </GokuDataGrid>
        </div>
    )
}

export default EmployeeAffiliateList
